import { EmailAccount } from './../interfaces/emailAccount';
import AudiencesModel from './AudiencesModel';
import { CtmAdModel } from './CtmAdModel';
import { PromoterModel } from './PromoterModel';
import TriggerModel from './TriggerModel';

type PaginationType = {
  isLastPage: boolean;
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
};

export type QueuedContactType = {
  name: string;
  scheduledAt: string;
  offset: string;
};

export type QueuedContactsType = {
  contacts: QueuedContactType[];
  pagination: PaginationType;
};

export type CampaignSchedulerAttribute = {
  currentQueuedCount?: number;
  deliveredCount?: number;
  destinationPageId: number;
  destinationPageName: string;
  destinationPageGroupId?: number;
  destinationPageSearchToken: string;
  performAtTime?: string;
  errorCount?: number;
  id?: number;
  intervalInMin: number;
  readCount?: number;
  readOverTime?: {
    [key: number]: number;
  };
  respondedCount?: number;
  respondedOverTime?: {
    [key: number]: number;
  };
  scheduledAt?: string;
  sortOrder: number;
  unsubscribedCount?: number;
  destroy?: boolean;
  smsCurrentQueuedCount?: number;
  smsDeliveredCount?: number;
  smsRespondedCount?: number;
  smsReadOverTime?: {
    [key: number]: number;
  };
  smsRespondedOverTime?: {
    [key: number]: number;
  };
  instagramCurrentQueuedCount?: number;
  instagramDeliveredCount?: number;
  instagramRespondedCount?: number;
  instagramReadCount?: number;
  instagramUnsubscribedCount?: number;
  instagramReadOverTime?: {
    [key: number]: number;
  };
  instagramRespondedOverTime?: {
    [key: number]: number;
  };
  webchatCurrentQueuedCount?: number;
  testNumbers?: string[];
};

export const CampaignAudienceAttributeExclusionKeys = [
  'visitLastDays',
  'emailReceivedLastDays',
  'emailClickedLastDays',
  'purchaseLastDays',
  'audienceIds',
  'crmSynced',
] as const;

export type CampaignAudienceAttributeExclusionType =
  typeof CampaignAudienceAttributeExclusionKeys[number];

export type CampaignAudienceExclusions = {
  [K in CampaignAudienceAttributeExclusionType]?: number | number[] | string[];
};

export type CampaignAudienceAttribute = CampaignAudienceExclusions & {
  audienceId: number;
  kind: 'included' | 'excluded';
  destroy?: boolean;
  contactsCount?: number;
  name?: string;
  groupName?: string;
  triggerType?: 'trigger' | 'promoter' | 'page' | 'ctmAd';
  promoter?: PromoterModel;
  trigger?: TriggerModel;
  ctmAd?: CtmAdModel;
  usedInAutomationId?: number;
  exclusionsSettings?: boolean;
};

export type CampaignWeekdayOrdinal = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export type CampaignAcceptableTime = {
  daysOfWeek?: CampaignWeekdayOrdinal[];
  startTime?: string;
  stopTime?: string;
};

// status list isn't complete - just used for static type checking
export type CampaignStatuses =
  | 'queued'
  | 'sent'
  | 'canceled'
  | 'sending'
  | 'paused'
  | 'failed'
  | 'delivered';

export type PlatformType =
  | 'sms'
  | 'facebook'
  | 'instagram'
  | 'web_chat'
  | 'email'
  | string;

export type CampaignScheduleType =
  | 'none'
  | 'global'
  | 'drip'
  | 'timezone'
  | 'automation';

export type SlackDataType = {
  teamId: string | null;
  teamName: string | null;
  assetUrl: string | null;
  botName: string | null;
  sendAsBot: boolean;
  slackChannelId: string | null;
  slackChannelName: string | null;
  content: string | null;
  active?: boolean;
};

export type SoldConfigurationsType = {
  defaultConfiguration?: boolean;
  unicorn: {
    open: number;
    click: number;
    reply: number;
    notifications: {
      defaultConfiguration?: boolean;
      rule: { open: number; click: number; reply: number };
      sms: { to: string[]; content?: string; active?: boolean };
      desktopPush: { to: number[]; active?: boolean };
      mobilePush: { to: number[]; active?: boolean };
      slack: SlackDataType;
      email: {
        to: string[];
        content?: string;
        subject?: string;
        active?: boolean;
      };
    };
  };
  donkey: {
    notOpen: number;
    notClick: number;
    notReply: number;
  };
  stopSendingAfterReply?: boolean;
  stopSendingAfterUnicorn?: boolean;
};

export type CampaignType = {
  active: boolean;
  acceptableTime: CampaignAcceptableTime;
  allowRequeued?: boolean;
  attemptToSendCount: number;
  audienceId?: number;
  audienceName?: string;
  audiences?: AudiencesModel[];
  botId: number;
  campaignAudiencesAttributes?: CampaignAudienceAttribute[];
  contactsCount: number;
  createdAt: string;
  currentQueuedCount: number;
  deliveredCount: number;
  smsDeliveredCount?: number;
  editable: boolean;
  errorCount: number;
  id: number;
  initContactsCount?: number;
  initUnsubscribedCount?: number;
  name: string;
  openRate: number;
  smsOpenRate?: number;
  purpose: number | string; // number when sending to API; string when receiving from API.
  purposeName?: string;
  readCount: number;
  relatedCampaigns?: CampaignType[];
  smsReadCount?: number;
  respondedCount: number;
  smsRespondedCount?: number;
  instagramRespondedCount?: number;
  instagramDeliveredCount?: number;
  responseRate: number;
  tags?: { name: string }[];
  smsResponseRate?: number;
  scheduleType: CampaignScheduleType;
  pauseReason: null | string; // drip campaign that was previously active in a free trial or sub
  cancelReason: null | string; //  // "Subscription:123" | "PromoCode: 213"
  platforms?: PlatformType[];
  searchToken: string;
  schedulersAttributes?: CampaignSchedulerAttribute[];
  statisticsReady?: boolean;
  statsArchived?: boolean | null;
  status: CampaignStatuses;
  system: boolean;
  src?: string;
  undeliverableCount: number;
  unsubscribedCount: number;
  userId: number;
  sentAt?: string;
  queuedAt?: string;
  sendingAt?: string;
  possibleCredits?: null | Record<PlatformType, number>;
  needsReview?: null | boolean; // for automation badge
  emailAccounts?: EmailAccount[];
  emailAccountsIds?: number[];
  suppressionListEnabled?: boolean;
  soldConfigurations: null | SoldConfigurationsType;
  sendCapacity?: number;
  details?: {
    automationTemplate: 'email_sold_automation' | 'empty_automation';
  };
  parentId?: number;
  totalSuppressedCount: number | null;
  totalQueuedCount: number | null;
  totalCurrentQueuedCount: number | null;
  totalErrorCount: number | null;
  totalSendCount: number | null;
  exclusionsCount: number | null;
};

export type TrackingsType = {
  name: string;
  remoteId: string;
  status: string;
};

export default interface CampaignsModel {
  pagination: PaginationType;
  campaigns: CampaignType[];
}

export interface CampaignTracking {
  pagination: PaginationType;
  trackings: TrackingsType[];
}
